import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import apiClient from '../../../services/apiClient';
const MAX_HOURS_REGEX = /^\d+(\.\d{1,2})?$/;
const MAX_HOURS_TYPING_REGEX = /^\d*(\.\d{0,2})?$/;
export default function AddDeveloperModal({
  onClose,
  onSubmit,
  loading = false,
  projectKey,
  weekCount = 12,
  weeks = [],
  roleTypes = [],
  roles = [],
  mode = 'add',
  initialData = null,
}) {
  const isEdit = mode === 'edit';

  const [jiraUsers, setJiraUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [search, setSearch] = useState(isEdit ? (initialData?.name ?? '') : '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(
    isEdit
      ? {
        account_id: initialData?.jira_account_id,
        display_name: initialData?.name,
        avatar_url: initialData?.avatar_url,
      }
      : null
  );
  const [selectedRoleTypeId, setSelectedRoleTypeId] = useState(
    isEdit ? (initialData?.role_type_id ?? null) : null
  );
  const [selectedRoleId, setSelectedRoleId] = useState(
    isEdit ? (initialData?.role_id ?? null) : null
  );
  const [maxHoursPerWeek, setMaxHoursPerWeek] = useState(
    isEdit ? String(initialData?.max_hours_per_week ?? '') : '40'
  );
  const [errors, setErrors] = useState({});
  const dropdownRef = useRef(null);

  // Fetch Jira users for the project
  const fetchUsers = useCallback(async () => {
    if (!projectKey) return;
    setLoadingUsers(true);
    try {
      const res = await apiClient.get('/jira/users', {
        params: { project_key: projectKey },
      });
      const users = res?.data ?? [];
      setJiraUsers(users.filter((u) => u.active && u.account_type === 'atlassian'));
    } catch (err) {
      toast.error('Failed to load Jira users');
      setJiraUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [projectKey]);

  useEffect(() => {
    if (!isEdit) fetchUsers();
  }, [fetchUsers, isEdit]);

  // Roles filtered by selected type
  const filteredRoles = useMemo(
    () => (selectedRoleTypeId == null ? roles : roles.filter((r) => r.role_type_id === selectedRoleTypeId)),
    [roles, selectedRoleTypeId]
  );

  // When type changes, clear role selection if it's not in the new list
  useEffect(() => {
    if (selectedRoleId == null) return;
    const allowed = filteredRoles.some((r) => r.id === selectedRoleId);
    if (!allowed) setSelectedRoleId(null);
  }, [filteredRoles, selectedRoleId]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Escape key handling
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        if (showDropdown) {
          setShowDropdown(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, onClose, showDropdown]);

  // Filtered users based on search
  const filteredUsers = useMemo(
    () =>
      jiraUsers.filter(
        (u) =>
          !search ||
          (u.display_name || '').toLowerCase().includes(search.toLowerCase()) ||
          (u.email || '').toLowerCase().includes(search.toLowerCase())
      ),
    [jiraUsers, search]
  );



  const hasCustomWeeklyDistribution = useMemo(() => {
    if (!isEdit || !initialData?.weeklyHours?.length) return false;
    const hours = initialData.weeklyHours;
    const first = hours[0];
    return hours.some((h) => Number(h) !== Number(first));
  }, [isEdit, initialData]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearch(user.display_name || '');
    setShowDropdown(false);
    setErrors((prev) => ({ ...prev, developer: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!selectedUser) newErrors.developer = 'Please select a team member';
    if (selectedRoleId == null) newErrors.role = 'Please select a role';
    if (!maxHoursPerWeek) {
      newErrors.maxHoursPerWeek = 'Max capacity per week is required';
    } else if (Number(maxHoursPerWeek) <= 0) {
      newErrors.maxHoursPerWeek = 'Must be a positive number';
    } else if (!MAX_HOURS_REGEX.test(maxHoursPerWeek)) {
      newErrors.maxHoursPerWeek = 'Max 2 decimal places allowed';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const actualWeekCount = weeks.length > 0 ? weeks.length : weekCount;
    const hoursPerWeek = maxHoursPerWeek && actualWeekCount > 0
      ? parseFloat((Number(maxHoursPerWeek) / actualWeekCount).toFixed(2))
      : 0;

    if (isEdit) {
      onSubmit({
        projectDeveloperId: initialData.id,
        weeklyHours: Array(actualWeekCount).fill(hoursPerWeek),
        maxHoursPerWeek: maxHoursPerWeek ? Number(maxHoursPerWeek) : null,
      });
    } else {
      onSubmit({
        id: selectedUser.account_id,
        name: selectedUser.display_name,
        avatar_url: selectedUser.avatar_url,
        roleId: selectedRoleId,
        weeklyHours: Array(actualWeekCount).fill(hoursPerWeek),
        maxHoursPerWeek: maxHoursPerWeek ? Number(maxHoursPerWeek) : null,
      });
    }
  };

  const isFormValid =
    selectedUser &&
    selectedRoleId != null &&
    maxHoursPerWeek &&
    Number(maxHoursPerWeek) > 0 &&
    /^\d+(\.\d{1,2})?$/.test(maxHoursPerWeek);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-developer-title"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
      onClick={() => !loading && onClose()}
    >
      <div
        className="rounded-xl w-full max-w-lg bg-white dark:bg-slate-800 shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <h3 id="add-developer-title" className="text-xl font-semibold text-slate-900 dark:text-white mb-5">
            {isEdit ? 'Edit team member' : 'Add team member'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Team member search */}
            <div className="relative z-20" ref={dropdownRef}>
              <label htmlFor="developer-search" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Team member *
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
                <input
                  id="developer-search"
                  type="text"
                  role="combobox"
                  aria-expanded={showDropdown}
                  aria-controls="developer-listbox"
                  aria-autocomplete="list"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setSelectedUser(null);
                    setShowDropdown(true);
                  }}
                  onFocus={() => !isEdit && setShowDropdown(true)}
                  disabled={loadingUsers || isEdit}
                  placeholder={loadingUsers ? 'Loading users...' : 'Search by name or email...'}
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed ${errors.developer ? 'border-red-500' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                    }`}
                />
              </div>

              {showDropdown && !loadingUsers && (
                <ul
                  id="developer-listbox"
                  role="listbox"
                  aria-label="Jira users"
                  className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto overscroll-contain rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg py-1 scrollbar-thin"
                >
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((u) => (
                      <li key={u.account_id} role="option" aria-selected={selectedUser?.account_id === u.account_id}>
                        <button
                          type="button"
                          onClick={() => handleSelectUser(u)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-3"
                        >
                          <img
                            src={u.avatar_url}
                            alt=""
                            className="size-7 rounded-full shrink-0"
                          />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 dark:text-white truncate">{u.display_name}</p>
                            {u.email && <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{u.email}</p>}
                          </div>
                        </button>
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No users found</li>
                  )}
                </ul>
              )}
              {errors.developer && <p className="mt-1 text-xs text-red-500">{errors.developer}</p>}
            </div>

            {/* Type (role type) */}
            <div>
              <label htmlFor="developer-type" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Type *
              </label>
              <select
                id="developer-type"
                value={selectedRoleTypeId ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : Number(e.target.value);
                  setSelectedRoleTypeId(val);
                  setSelectedRoleId(null);
                  setErrors((prev) => ({ ...prev, role: undefined }));
                }}
                disabled={isEdit}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed ${errors.role ? 'border-red-500' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                  }`}
              >
                <option value="" disabled>{roleTypes.length === 0 ? 'No types' : 'Select type'}</option>
                {roleTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Role (filtered by type) */}
            <div>
              <label htmlFor="developer-role" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Role *
              </label>
              <select
                id="developer-role"
                value={selectedRoleId ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : Number(e.target.value);
                  setSelectedRoleId(val);
                  setErrors((prev) => ({ ...prev, role: undefined }));
                }}
                disabled={selectedRoleTypeId == null || isEdit}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed ${errors.role ? 'border-red-500' : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                  }`}
              >
                <option value="" disabled>
                  {selectedRoleTypeId == null ? 'Select type first' : filteredRoles.length === 0 ? 'No roles' : 'Select role'}
                </option>
                {filteredRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              {errors.role && <p className="mt-1 text-xs text-red-500">{errors.role}</p>}
            </div>
            {/* Max hours per week */}
            <div>
              <label htmlFor="max-hours-per-week" className="block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                Max capacity per week (hrs) *
              </label>
              <input
                id="max-hours-per-week"
                type="text"
                inputMode="decimal"
                value={maxHoursPerWeek}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || MAX_HOURS_TYPING_REGEX.test(val)) {
                    setMaxHoursPerWeek(val);
                    setErrors((prev) => ({ ...prev, maxHoursPerWeek: undefined }));
                  }
                }}
                placeholder="e.g. 20 for part-time"
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${errors.maxHoursPerWeek
                  ? 'border-red-500'
                  : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white'
                  }`}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Full-time is typically 40h. Adjust for part-time members.
              </p>
              {errors.maxHoursPerWeek && <p className="mt-1 text-xs text-red-500">{errors.maxHoursPerWeek}</p>}
            </div>

            {/* Warning: saving will flatten custom weekly distribution */}
            {hasCustomWeeklyDistribution && (
              <div
                role="alert"
                className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2.5 text-xs text-amber-800 dark:text-amber-200"
              >
                <p className="font-medium mb-0.5">Custom weekly allocation will be reset</p>
                <p>
                  This member has custom per-week hours. Saving will distribute the max capacity evenly across all weeks and overwrite the existing allocation.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || loading}
                className="px-6 py-2.5 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <ArrowPathIcon className="size-4 animate-spin mr-2" />
                    {isEdit ? 'Saving...' : 'Adding...'}
                  </>
                ) : (
                  isEdit ? 'Save' : 'Add'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}